import React, { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Box, Text, Button, List, Select } from 'grommet'
import DateInput from './DateInput'
import TimeInput from './TimeInput'
import { Add, FormSubtract } from 'grommet-icons'
import { colorThemes, I18n, Constants } from 'galarm-config'
import { DateTimeUtils, AlarmUtils } from 'galarm-shared'
import { NavigationUtils } from 'galarm-ps-api'
import moment from 'moment-timezone'
import uuid from 'react-native-uuid'
import arrUniqBy from 'lodash/uniqBy'

const OccurrenceSummary = ({
  id,
  date,
  removeOccurrence,
  onDateChange,
  index
}) => {
  const colorScheme = useSelector(state => state.userSettings.webColorScheme)

  const handleDateChange = newDate => {
    onDateChange(newDate, id)
  }

  const handleTimeChange = newDate => {
    onDateChange(newDate, id)
  }

  return (
    <Box
      direction="row"
      align="center"
      justify="between"
      round="small"
      gap="xsmall">
      <DateInput
        value={date ? new Date(date) : null}
        onChange={handleDateChange}
      />
      <TimeInput
        value={date ? new Date(date).getTime() : null}
        onChange={handleTimeChange}
      />
      {index > 1 && (
        <Button
          icon={<FormSubtract color="white" />}
          onClick={() => removeOccurrence(id)}
          plain
          style={{
            backgroundColor: colorThemes.getColorTheme(colorScheme).primary,
            borderRadius: '50%',
            padding: '5px'
          }}
        />
      )}
    </Box>
  )
}

const SelectSpecificDatesRepetition = props => {
  const [repeatOption, setRepeatOption] = useState(() => {
    const { repeatOption: parsedRepeatOption } =
      AlarmUtils.parseSpecificDatesRepeat(props.repeat || '')

    // Default to ONCE for legacy repeat strings that don't have a repeat option prefix, or if parsing fails
    const finalRepeatOption = parsedRepeatOption || Constants.RepeatOptions.ONCE

    return finalRepeatOption
  })

  const [occurrences, setOccurrences] = useState(() => {
    const { dateStrings } = AlarmUtils.parseSpecificDatesRepeat(
      props.repeat || ''
    )

    // If no parsed dates, create default occurrence
    if (dateStrings.length === 0) {
      const defaultDate = props.date || moment().add(1, 'hour').valueOf()
      const id = uuid.v4()
      return [
        {
          id,
          date: defaultDate
        }
      ]
    }

    const validOccurrences = dateStrings.map(dateStr => {
      const dateValue = DateTimeUtils.getDateFromFormattedDateString(
        dateStr.trim(),
        props.creatorTimezone
      )
      return { id: uuid.v4(), date: dateValue }
    })
    return validOccurrences
  })

  const onSaveRepetition = useCallback(() => {
    const validOccurrences = occurrences.filter(o => o.date)
    const uniqueOccurrences = arrUniqBy(validOccurrences, item =>
      item.date instanceof Date ? item.date.getTime() : item.date
    )
    if (uniqueOccurrences.length === 0) {
      NavigationUtils.showAlert(I18n.t('chooseAtLeastOneOccurrence'))
      return
    }
    uniqueOccurrences.sort((a, b) => a.date - b.date)

    // Convert occurrences to a date string using current timezone
    const occurrenceString = uniqueOccurrences
      .map(o =>
        DateTimeUtils.getFormattedDateAsStringInTimezone(
          o.date,
          props.creatorTimezone
        )
      )
      .join(',')

    // Create the repeat string with the repeat option prefix using double colon format
    const repeatString = `${repeatOption}${Constants.RepeatSeparator.SPECIFIC_DATES}${occurrenceString}`

    // Trigger the callback to save the changes with the repeat string
    props.onSaveAlarmRepetition(
      Constants.RepeatTypes.SPECIFIC_DATES,
      repeatString
    )
  }, [occurrences, repeatOption, props.onSaveAlarmRepetition])

  useEffect(() => {
    onSaveRepetition()
  }, [occurrences, repeatOption, onSaveRepetition])

  const getAvailableRepeatOptions = useCallback(() => {
    return AlarmUtils.getAvailableRepeatOptionsForSpecificDates(
      occurrences,
      props.creatorTimezone
    )
  }, [occurrences])

  useEffect(() => {
    const availableOptions = getAvailableRepeatOptions()
    if (!availableOptions.includes(repeatOption)) {
      setRepeatOption(Constants.RepeatOptions.ONCE)
    }
  }, [occurrences, repeatOption, getAvailableRepeatOptions])

  const addOccurrence = () => {
    setOccurrences(prevOccurrences => {
      const newOccurrence = {
        id: uuid.v4(),
        date:
          prevOccurrences.length > 0
            ? prevOccurrences[prevOccurrences.length - 1].date
            : props.date || moment().add(1, 'hour').valueOf()
      }
      const updatedOccurrences = [...prevOccurrences, newOccurrence]
      return updatedOccurrences
    })
  }

  const removeOccurrence = id => {
    setOccurrences(prevOccurrences => prevOccurrences.filter(o => o.id !== id))
  }

  const onDateChange = (newDate, id) => {
    let timestamp
    if (newDate instanceof Date) {
      timestamp = newDate.getTime()
    } else if (typeof newDate === 'number') {
      timestamp = newDate
    } else if (newDate) {
      timestamp = new Date(newDate).getTime()
    } else {
      return
    }
    setOccurrences(prevOccurrences =>
      prevOccurrences.map(o =>
        o.id === id
          ? {
              ...o,
              date: timestamp
            }
          : o
      )
    )
  }

  return (
    <Box overflow="auto">
      <Text margin={'small'}>{I18n.t('addOrRemoveAlarmOccurrences')}</Text>
      <Box
        direction="row"
        align="center"
        justify="between"
        round="small"
        margin="small"
        gap="small">
        <Button
          icon={<Add />}
          label={I18n.t('addOccurrence')}
          onClick={addOccurrence}
          alignSelf="center"
          margin="xsmall"
        />
        <Select
          options={getAvailableRepeatOptions()}
          value={repeatOption}
          onChange={({ option }) => setRepeatOption(option)}
          labelKey={option => AlarmUtils.getRepeatOptionLabel(option)}
          size="medium"
          margin="small"
        />
      </Box>
      <List data={occurrences} border={false}>
        {({ id, date }) => (
          <OccurrenceSummary
            id={id}
            date={date}
            removeOccurrence={removeOccurrence}
            onDateChange={onDateChange}
            index={occurrences.length}
          />
        )}
      </List>
    </Box>
  )
}

export default SelectSpecificDatesRepetition
